import { useContext, useEffect, useState } from "react";
import styles from "./styles/PosTicketResult.module.css";
import DataContext from "../../../context/DataContext";
import { useTranslation } from "react-i18next";
import Spinner from "../../../components/ui/spinner/Spinner";
import useCurrencySymbol from "../../../hooks/useCurrencySymbol";
import { useNavigate, useParams } from "react-router-dom";
import POSDataContext from "../../../context/POSDataContext";
import PosTicketInfo from "./PosTicketInfo";
import PosTicketPanel from "./PosTicketPanel";

/**
 * Ticket result page
 */
const PosTicketResult = () => {
    const { bUser } = useParams(); // Extracts 'bUser' parameter from the URL.

    const [showTicketInfo, setShowTicketInfo] = useState(false);
    const [notReadyView, setNotReadyView] = useState(true);

    const {
        currentCustom, // {Object} - The current client customizations
        isServiceDataAvailable, // {Boolean} - Indicates if the service data is available
        serviceData, // {Function} - Fetch the service data
        currentBU, // {String} - The current business user
        isLoading,
        JWTToken,
    } = useContext(DataContext);

    const {
        currentTicketPos, // {Object} - The current ticket data for POS
        isLoadingPos, // {Boolean} - Indicates if the POS data is loading
        isValidSearchPos, // {Boolean} - Indicates if the search is valid in POS
        fetchTickets, // {Function} - Function to fetch tickets data
        filterExpression, // {String} - The filter expression for ticket search
        resultMount, // {Boolean} - Indicate if this is the first mount for the page, used to show the spinner
        // in the page backgroud on the first mount and inside the pannel when the tickets are updated
        setRresultMount, // {Function} - Function to set the result mount value
        selectedInstalmentsPos,
        setSelectedInstalmentsPos,
        setSelectedTicketsPos,
        selectedTicketsPos,
        ticketTypePos,
        responseDataPos,
    } = useContext(POSDataContext);

    const currencySymbol = useCurrencySymbol(true); // Custom hook to get the currency symbol based on current settings.
    const navigate = useNavigate(); // Hook for navigation in React Router.

    const [selectedSettlementsPos, setSelectedSettlementsPos] = useState([]);

    const [clickedInstalmentPos, setClickedInstalmentPos] = useState(null);
    const [currenInstalmentPos, setCurrenInstalmentPos] = useState(null);

    const handleClickedInstalment = (number) => {
        setClickedInstalmentPos(number);
    };

    // Redirect to main page if no service data or no filter expression
    useEffect(() => {
        if (!isServiceDataAvailable || !filterExpression) {
            navigate(`/${bUser}/pos`);
        }
    }, [bUser, isServiceDataAvailable, filterExpression]);

    // Fetch servide data
    useEffect(() => {
        bUser &&
            JWTToken &&
            (!isServiceDataAvailable || bUser !== currentBU) &&
            serviceData(bUser);
    }, [bUser, isServiceDataAvailable, JWTToken]);

    // Fetch data when updating tickets but not on the first mount
    // this is used when refreshing the page
    // the tickets will be updated using the expressiion and the spinner will be inside the panel
    useEffect(() => {
        if (resultMount) {
            setRresultMount(false);
        } else if (isValidSearchPos && filterExpression && JWTToken) {
            fetchTickets(filterExpression, 1);
        }
    }, [isValidSearchPos, bUser, JWTToken]);

    // Reset selected instalments and selected tickets
    useEffect(() => {
        setSelectedTicketsPos([]);

        setSelectedInstalmentsPos([]);
    }, []);

    // Check ready for viewing
    useEffect(() => {
        if (isServiceDataAvailable && (!isLoadingPos || !isLoading)) {
            const timer = setTimeout(() => {
                setNotReadyView(false);
            }, 50);

            // Cleanup function
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isServiceDataAvailable, isLoadingPos, isLoading]);

    // Update the ticket value when charges are updated
    useEffect(() => {
        if (!responseDataPos || !selectedTicketsPos?.length) return;

        const allTickets = [
            responseDataPos?.ticket,
            ...(responseDataPos?.relatedTickets ?? []),
        ].filter(Boolean);

        if (!allTickets.length) return;

        const byId = new Map(allTickets.map((t) => [String(t.ticketID), t]));
        setSelectedTicketsPos((prev) =>
            (prev ?? []).map((sel) => {
                const incoming = byId.get(String(sel.ticketID));
                return incoming
                    ? { ...sel, amount: incoming.paymentBalance }
                    : sel;
            }),
        );
    }, [responseDataPos]);

    const handleCheckInstalment = (event) => {
        event.stopPropagation(); // Prevent event bubbling

        setSelectedInstalmentsPos((prevSelected = []) => {
            // Ensure prevSelected is an array
            const isSelected = prevSelected.some(
                (inst) => inst.instalmentId === event.target.name,
            );

            if (isSelected) {
                if (prevSelected.length < 2) {
                    return prevSelected;
                } else
                    return prevSelected.filter(
                        (inst) => inst.instalmentId !== event.target.name,
                    ); // Remove if exists
            } else {
                return [
                    ...prevSelected,
                    {
                        instalmentId: event.target.name,
                        amount: event.target.value,
                        instalmentInfo: event.target.dataset.instinfo,
                        instalmentDate: event.target.dataset.instalmentdate,
                        instalmentTransactionID:
                            event.target.dataset.insttransactionid,
                    },
                ];
            }
        });
    };

    const handleCheckSettlement = (event) => {
        if (event && event.stopPropagation) {
            event.stopPropagation();
        }

        setSelectedSettlementsPos((prevSelected) =>
            prevSelected.includes(event.target.name)
                ? prevSelected.filter((caseNo) => caseNo !== event.target.name)
                : [...prevSelected, event.target.name],
        );
    };

    return (
        <article
            className={`${styles.main_search_wrapper} transition-container 
                    ${
                        notReadyView
                            ? "transition-hidden"
                            : "transition-visible"
                    } main-page-container flex-container`}
        >
            {!isServiceDataAvailable || isLoadingPos ? (
                <Spinner />
            ) : (
                <main
                    className={`${styles.main_search_container}   main-container flex-container`}
                >
                    <PosTicketPanel
                        setShowTicketInfo={setShowTicketInfo}
                        selectedSettlementsPos={selectedSettlementsPos}
                        selectedInstalmentsPos={selectedInstalmentsPos}
                        setSelectedInstalmentsPos={setSelectedInstalmentsPos}
                        handleCheckInstalment={handleCheckInstalment}
                        handleCheckSettlement={handleCheckSettlement}
                        currency={currencySymbol}
                        clickedInstalmentPos={clickedInstalmentPos}
                        setClickedInstalmentPos={setClickedInstalmentPos}
                        currenInstalmentPos={currenInstalmentPos}
                        setCurrenInstalmentPos={setCurrenInstalmentPos}
                        handleClickedInstalment={handleClickedInstalment}
                    />

                    {showTicketInfo ? (
                        <div
                            className={`${styles.main_center_panel} v-flex-container`}
                        >
                            <PosTicketInfo
                                ticket={currentTicketPos}
                                color={currentCustom?.color?.current}
                                currency={currencySymbol}
                                selectedSettlementsPos={selectedSettlementsPos}
                                selectedInstalmentsPos={selectedInstalmentsPos}
                                handleCheckInstalment={handleCheckInstalment}
                                ticketTypePos={ticketTypePos}
                                currenInstalmentPos={currenInstalmentPos}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                </main>
            )}
        </article>
    );
};

export default PosTicketResult;
